/**
 * System Health Card Component
 * 
 * Displays a health status card with visual indicators for system components
 */

import React from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { 
  Activity, 
  AlertTriangle, 
  CheckCircle2, 
  XCircle,
  TrendingUp,
  TrendingDown,
  Loader2
} from 'lucide-react'

interface SystemHealthCardProps {
  title: string
  description?: string
  status: 'healthy' | 'warning' | 'error' | 'unknown'
  metrics?: {
    label: string
    value: string | number
    trend?: 'up' | 'down' | 'neutral'
    status?: 'good' | 'warning' | 'error'
  }[]
  details?: React.ReactNode
  isLoading?: boolean
  className?: string
  icon?: React.ReactNode
}

export default function SystemHealthCard({
  title,
  description,
  status,
  metrics = [],
  details,
  isLoading = false,
  className,
  icon
}: SystemHealthCardProps) {
  const statusConfig = {
    healthy: {
      icon: CheckCircle2,
      color: 'text-green-600 dark:text-green-400',
      bgColor: 'bg-green-50 dark:bg-green-950',
      borderColor: 'border-green-200 dark:border-green-800',
      badge: 'Healthy',
      badgeVariant: 'default' as const
    },
    warning: {
      icon: AlertTriangle,
      color: 'text-yellow-600 dark:text-yellow-400',
      bgColor: 'bg-yellow-50 dark:bg-yellow-950',
      borderColor: 'border-yellow-200 dark:border-yellow-800',
      badge: 'Warning',
      badgeVariant: 'secondary' as const
    },
    error: {
      icon: XCircle,
      color: 'text-red-600 dark:text-red-400',
      bgColor: 'bg-red-50 dark:bg-red-950',
      borderColor: 'border-red-200 dark:border-red-800',
      badge: 'Error',
      badgeVariant: 'destructive' as const
    },
    unknown: {
      icon: Activity,
      color: 'text-gray-600 dark:text-gray-400',
      bgColor: 'bg-gray-50 dark:bg-gray-950',
      borderColor: 'border-gray-200 dark:border-gray-800',
      badge: 'Unknown',
      badgeVariant: 'outline' as const
    }
  }

  const config = statusConfig[status]
  const StatusIcon = config.icon

  if (isLoading) {
    return (
      <Card className={cn('relative overflow-hidden', className)}>
        <CardHeader>
          <CardTitle className="flex items-center gap-3">
            {icon || <Activity className="h-5 w-5" />}
            <span>{title}</span>
          </CardTitle>
          {description && <CardDescription>{description}</CardDescription>}
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className={cn(
      'relative overflow-hidden transition-all',
      config.borderColor,
      'border-2',
      className
    )}>
      {/* Status indicator bar */}
      <div className={cn('absolute inset-x-0 top-0 h-1', {
        'bg-green-500': status === 'healthy',
        'bg-yellow-500': status === 'warning',
        'bg-red-500': status === 'error',
        'bg-gray-500': status === 'unknown'
      })} />

      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={cn(
              'p-2 rounded-lg',
              config.bgColor
            )}>
              {icon || <StatusIcon className={cn('h-5 w-5', config.color)} />}
            </div>
            <div>
              <CardTitle className="text-lg">{title}</CardTitle>
              {description && (
                <CardDescription className="mt-1">{description}</CardDescription>
              )}
            </div>
          </div>
          <Badge variant={config.badgeVariant}>
            {config.badge}
          </Badge>
        </div>
      </CardHeader>

      {(metrics.length > 0 || details) && (
        <CardContent className="space-y-4">
          {metrics.length > 0 && (
            <div className="grid grid-cols-2 gap-4">
              {metrics.map((metric, index) => (
                <div key={index} className="space-y-1">
                  <p className="text-sm text-muted-foreground">{metric.label}</p>
                  <div className="flex items-center gap-2">
                    <p className={cn(
                      'text-2xl font-semibold',
                      metric.status === 'error' && 'text-red-600 dark:text-red-400',
                      metric.status === 'warning' && 'text-yellow-600 dark:text-yellow-400',
                      metric.status === 'good' && 'text-green-600 dark:text-green-400'
                    )}>
                      {metric.value}
                    </p>
                    {metric.trend && (
                      <div className={cn(
                        'flex items-center gap-1 text-sm',
                        metric.trend === 'up' && 'text-green-600 dark:text-green-400',
                        metric.trend === 'down' && 'text-red-600 dark:text-red-400'
                      )}>
                        {metric.trend === 'up' ? (
                          <TrendingUp className="h-4 w-4" />
                        ) : metric.trend === 'down' ? (
                          <TrendingDown className="h-4 w-4" />
                        ) : null}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {details && (
            <div className={cn(
              'rounded-lg p-3',
              config.bgColor
            )}>
              {details}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  )
}

// Specialized health indicator component
export function HealthIndicator({ 
  status, 
  label,
  size = 'sm' 
}: { 
  status: 'healthy' | 'warning' | 'error' | 'unknown'
  label?: string
  size?: 'sm' | 'md' | 'lg'
}) {
  const sizeClasses = {
    sm: 'h-2 w-2',
    md: 'h-3 w-3',
    lg: 'h-4 w-4'
  }

  return (
    <div className="flex items-center gap-2">
      <div className={cn(
        'rounded-full animate-pulse',
        sizeClasses[size],
        {
          'bg-green-500': status === 'healthy',
          'bg-yellow-500': status === 'warning',
          'bg-red-500': status === 'error',
          'bg-gray-500': status === 'unknown'
        }
      )} />
      {label && <span className="text-sm text-muted-foreground">{label}</span>}
    </div>
  )
}